import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  Autocomplete,
  FormControl,
  InputLabel,
  CircularProgress,
  Grid,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import InputAdornment from "@mui/material/InputAdornment";
import { decoratedFetch } from "../../req_utils";
import { useParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import { SUPPORT_EMAIL } from "../../constants";
import { useSelector } from "react-redux";

const LABEL_COLOR = "#7A7A7A";
const BORDER_COLOR = "#C5C5C5";

export function CreateFreightItemDialog({
  isOpen,
  handleClose: handleCloseProp,
  freightItem,
  handleSubmit: handleSubmitProp,
  setSnackbarOpen,
  setSnackbarMessage,
  proposalId,
}) {
  let { project_id } = useParams();
  const employee = useSelector((state) => state.employee);
  const employee_branch_id = useSelector((state) => state.employee_branch_id);
  const [type, setType] = useState("");
  const [types, setTypes] = useState([]);
  const [quantity, setQuantity] = useState(0);
  const [displayQuantity, setDisplayQuantity] = useState("");
  const [unit, setUnit] = useState("");
  const [units, setUnits] = useState([]);
  const [unitPrice, setUnitPrice] = useState("");
  const [totalPrice, setTotalPrice] = useState(parseFloat(0).toFixed(2));
  const [ship_to, setShipTo] = useState("");
  const [ship_from, setShipFrom] = useState("");
  const [description, setDescription] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [savedFreightItems, setSavedFreightItems] = useState([]);
  const [savedFreight, setSavedFreight] = useState({});
  const [isAddToSavedChecked, setIsAddToSavedChecked] = useState(false);

  const handleCheckboxChange = (event) => {
    setIsAddToSavedChecked(event.target.checked);
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (freightItem) {
      initializeFromFreightItemProp();
    } else {
      resetFields();
    }
    decoratedFetch("/list_freight_item_types")
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Unknown error");
        }
        return response.json();
      })
      .then((data) => setTypes(data))
      .catch((error) =>
        console.error(`Error fetching freight item types ${error}`),
      );
    decoratedFetch("/list_size_units")
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Unknown error");
        }
        return response.json();
      })
      .then((data) => setUnits(data))
      .catch((error) => console.error(`Error fetching size units ${error}`));
    decoratedFetch(`/list_saved_freight_items?branch_id=${employee_branch_id}`)
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Unknown error");
        }
        return response.json();
      })
      .then((data) => {
        setSavedFreightItems(data);
      })
      .catch((error) => {
        console.error(`Error fetching saved freight items: ${error}`);
      });
  }, [isOpen]);

  useEffect(() => {
    if (savedFreight && Object.keys(savedFreight).length > 0) {
      setType(savedFreight.freight_item_type || "");
      setUnit(savedFreight.size_unit?.id || "");
      setUnitPrice(savedFreight.unit_price || "");
      setDescription(savedFreight.description || "");
      setShipFrom(savedFreight.ship_from || "");
      setShipTo(savedFreight.ship_to || "");
    }
  }, [savedFreight]);

  useEffect(() => {
    setTotalPrice((quantity * parseFloat(unitPrice || 0)).toFixed(2));
  }, [quantity, unitPrice]);

  const resetFields = () => {
    setType("");
    setQuantity(0);
    setDisplayQuantity("");
    setUnit("");
    setUnitPrice("");
    setDescription("");
    setShipFrom("");
    setShipTo("");
    setTotalPrice(parseFloat(0).toFixed(2));
    setLoading(false);
    setAlertMessage("");
    setSavedFreight(null);
    setIsAddToSavedChecked(false);
  };

  const initializeFromFreightItemProp = () => {
    setType(freightItem.freight_item_type);
    setQuantity(freightItem?.quantity);
    setDisplayQuantity(freightItem?.quantity.toString());
    setUnit(freightItem?.size_unit.id);
    setUnitPrice(freightItem?.unit_price);
    setTotalPrice(freightItem?.total_price);
    setDescription(freightItem?.description);
    setShipFrom(freightItem?.ship_from);
    setShipTo(freightItem?.ship_to);
    setSavedFreight(null);
    setIsAddToSavedChecked(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setLoading(true);

    if (!type || !unit) {
      setAlertMessage(
        "Required fields not filled in. Item must have a type and unit",
      );
      setLoading(false);
      return;
    }

    // First, create/update the project freight item
    const api = freightItem
      ? `/update_project_freight_item/${freightItem.id}`
      : "/create_project_freight_item";

    const projectFreightPromise = decoratedFetch(api, {
      method: freightItem ? "PUT" : "POST",
      body: JSON.stringify({
        project_id: project_id,
        proposal_id: proposalId,
        type: type,
        description: description,
        ship_from: ship_from,
        ship_to: ship_to,
        quantity: quantity,
        unit: unit,
        unit_price: unitPrice ? unitPrice : parseFloat(0).toFixed(2),
        total_price: totalPrice,
      }),
    });

    // If checkbox is checked, also save to saved freight items (without quantity)
    let savedFreightPromise = Promise.resolve();
    if (isAddToSavedChecked) {
      savedFreightPromise = decoratedFetch("/create_saved_freight_item", {
        method: "POST",
        body: JSON.stringify({
          freight_item_type: type,
          unit: unit,
          unit_price: unitPrice ? unitPrice : parseFloat(0).toFixed(2),
          ship_to: ship_to,
          ship_from: ship_from,
          description: description,
          branch_id: employee_branch_id,
        }),
      });
    }

    // Wait for both operations to complete
    Promise.all([projectFreightPromise, savedFreightPromise])
      .then(([projectResponse, savedResponse]) => {
        // Check project freight item response
        if (!freightItem && projectResponse.status === 201) {
          return;
        }
        if (freightItem && projectResponse.status === 200) {
          return;
        }

        // Handle project freight item errors
        switch (projectResponse.status) {
          case 400:
            return projectResponse.json().then((errorPayload) => {
              if (errorPayload.msg) {
                setAlertMessage(errorPayload.msg);
              }
              throw new Error();
            });
            break;
          case 500:
            setAlertMessage(
              `Internal server error. If this persists, please contact ${SUPPORT_EMAIL}`,
            );
            break;
          default:
            setAlertMessage(
              `Unknown error. If this persists, please contact ${SUPPORT_EMAIL}`,
            );
        }
        throw new Error();
      })
      .then(() => {
        setLoading(false);
        handleSubmitProp(
          parseFloat(totalPrice),
          freightItem ? parseFloat(freightItem.total_price) : 0,
          proposalId,
        );
        handleClose();
        setSnackbarOpen(true);
        setSnackbarMessage(`Freight ${freightItem ? "updated" : "created"}`);
      })
      .catch((error) => {
        console.error(
          `Error ${freightItem ? "editing" : "creating"} freight item`,
        );
        setLoading(false);
      });
  };

  const handleClose = (e, reason) => {
    if (reason === "backdropClick") {
      return;
    }
    resetFields();
    setAlertMessage("");
    handleCloseProp();
  };

  const handleAlertClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMessage("");
  };

  return (
    <Dialog disableScrollLock open={isOpen} onClose={handleClose}>
      <DialogTitle sx={{ fontSize: "22px", paddingBottom: "0px", flexGrow: 1 }}>
        {freightItem ? "Edit" : "Create"} Item
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} direction="row" marginTop={1}>
          {alertMessage && (
            <Grid item xs={12}>
              <Alert
                onClose={handleAlertClose}
                severity="error"
                sx={{ width: "100%", borderRadius: 8 }}
              >
                {alertMessage}
              </Alert>
            </Grid>
          )}
          {!freightItem && (
            <Grid item xs={12}>
              <Autocomplete
                id="saved-freight-autocomplete"
                options={savedFreightItems}
                getOptionLabel={(option) =>
                  `${option.freight_item_type || ""} ${option?.description || ""} ${option?.ship_from || ""} ${option?.ship_to || ""} ${option?.size_unit?.name || ""} ${option?.unit_price || ""}`
                }
                value={savedFreight}
                onChange={(event, newValue) => {
                  setSavedFreight(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Saved Freight Item"
                    variant="outlined"
                    fullWidth
                    placeholder="Select saved freight item"
                  />
                )}
              />
            </Grid>
          )}
          <Grid item xs={6}>
            <Autocomplete
              id="Type"
              value={type}
              freeSolo
              options={types.map((option) => option.display_name)}
              renderInput={(params) => <TextField {...params} label="Type" />}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === "input") {
                  setType(newInputValue);
                }
              }}
              onChange={(event, newValue) => {
                if (typeof newValue === "string") {
                  setType(newValue);
                }
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Description"
              fullWidth
              variant="outlined"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Ship To"
              fullWidth
              variant="outlined"
              value={ship_to}
              onChange={(e) => setShipTo(e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Ship From"
              fullWidth
              variant="outlined"
              value={ship_from}
              onChange={(e) => setShipFrom(e.target.value)}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Quantity"
              type="number"
              value={displayQuantity}
              onChange={(e) => {
                const displayVal = e.target.value;
                const floatVal = displayVal
                  ? parseFloat(displayVal).toFixed(2)
                  : 0;
                if (floatVal < 0) {
                  return;
                }
                setDisplayQuantity(displayVal);
                setQuantity(floatVal);
              }}
              InputLabelProps={{ shrink: displayQuantity }}
              fullWidth
            />
          </Grid>
          <Grid item xs={4}>
            <FormControl
              variant="outlined"
              sx={{
                minWidth: 120,
                width: "100%",
              }}
            >
              <InputLabel>Unit</InputLabel>
              <Select
                id="unit-select"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                label="Unit"
                fullWidth
              >
                {units.map((unit) => (
                  <MenuItem key={unit.id} value={unit.id}>
                    {unit.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Unit Price"
              type="number"
              value={unitPrice}
              onChange={(e) => {
                const val = e.target.value;
                if (val < 0) {
                  return;
                }
                setUnitPrice(val);
              }}
              onBlur={() =>
                setUnitPrice(parseFloat(unitPrice).toFixed(2).toString())
              }
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            {project_id && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAddToSavedChecked}
                    onChange={handleCheckboxChange}
                  />
                }
                label="Add to Saved Freight Items"
                sx={{ width: "100%" }}
              />
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "space-between",
          margin: "5px",
          marginLeft: "10px",
        }}
      >
        <Typography
          variant="p"
          id="total-freight-item-price"
          sx={{ float: "left" }}
        >
          Total price: <b>${totalPrice}</b>
        </Typography>
        {loading ? (
          <CircularProgress
            size="25px"
            sx={{ textAlign: "center", mt: "6px", mb: "5.5px", mr: "20px" }}
          />
        ) : (
          <div>
            <Button
              sx={{ marginRight: "10px" }}
              variant="outlined"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={alertMessage}
            >
              {freightItem ? "Save" : "Create"}
            </Button>
          </div>
        )}
      </DialogActions>
    </Dialog>
  );
}
